import { defineMessages } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";

import TourDescriptor from "@app/component/flows/types/TourDescriptor";
import waitForElement from "@app/component/flows/utils/waitForElement";
import { getApiService } from "@app/store/api";
import { showEntityView } from "@app/store/entityView/actions";
import { showGlobalSearch } from "@app/store/globalSearch/actions";
import { getOrganizationId } from "@app/store/iam";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import companyFieldModel from "@app/util/fieldModel/CompanyFieldModel";
import { createAndCondition, createSimpleCondition } from "@app/util/filters/utils";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import { checkEmailMenuItem, checkForCompaniesWithEmail, closeComposeModal } from "./common";

const messages = defineMessages({
  step01a: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step01a",
    defaultMessage: "Sending an Individual Email from a Record",
  },
  step01aDescription: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step01a.description",
    defaultMessage:
      "Use this when sending to a single contact from their specific record.{br}{br}Let's open any record via Search. Feel free to select any record with an email.",
  },
  step01b: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step01b",
    defaultMessage: "Sending an Individual Email from a Record",
  },
  step01bDescription: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step01b.description",
    defaultMessage:
      "Use this when sending to a single contact from their specific record.{br}{br}Here we picked a random record with an email.",
  },
  step02: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step02",
    defaultMessage: "Menu",
  },
  step02Description: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step02.description",
    defaultMessage: "Open the menu by clicking on triple dots item",
  },
  step03: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step03",
    defaultMessage: "Email",
  },
  step03Description: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step03.description",
    defaultMessage: "Click the Email submenu.",
  },
  step04: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step04",
    defaultMessage: "Email Button",
  },
  step04Description: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step04.description",
    defaultMessage: "Select Send via MMC",
  },
  step05: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step05",
    defaultMessage: "",
  },
  step05Description: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step05.description",
    defaultMessage: "",
  },
  step06: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step06",
    defaultMessage: "No emails",
  },
  step06Description: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step06.description",
    defaultMessage:
      "In case some selected records have no emails, this modal will show up. Click Continue to proceed.",
  },
  step07: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step07",
    defaultMessage: "Record type",
  },
  step07Description: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step07.description",
    defaultMessage:
      "Select who you want to email in the modal that pops up.{br}{br}Pick either option and click Continue.",
  },
  step08: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step08",
    defaultMessage: "Compose your email",
  },
  step08Description: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step08.description",
    defaultMessage:
      "✉️ Add a Subject line{br}✍️ Write your Message - You can use the toolbar icons at the bottom left to insert a hyperlink, file, resource or dynamic variable.",
  },
  step09: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step09",
    defaultMessage: "Send your email",
  },
  step09Description: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step09.description",
    defaultMessage:
      "If you have disabled the Open Activity Form setting, the button will show Send and Create.{br}If enabled or required fields exist, click Next to complete the activity form and then finalize.",
  },
  step10: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step10",
    defaultMessage: "Tour Complete",
  },
  step10Description: {
    id: "guides.sendingEmailsViaRecordPreview.tour.step10.description",
    defaultMessage: "🎉 Congratulations! You've completed this tour.",
  },
});

export const sendingViaRecordPreviewTourConfiguration: TourDescriptor = {
  id: "mmc-sending-emails-via-record-preview",
  steps: [
    {
      id: "start",
      onBefore: async ({ store }) => {
        const result = await checkForCompaniesWithEmail(store);
        if (result) {
          return result;
        }

        const filterModel = createAndCondition([
          createSimpleCondition(CommonFieldName.EMAIL, FilterOperator.NOT_EMPTY),
        ]);

        const apiService = getApiService(store.getState());
        const orgId = getOrganizationId(store.getState());
        const response = await apiService!.fetchCompanies(orgId!, {
          $columns: ["id"],
          $filters: convertToPlatformFilterModel(filterModel, [], companyFieldModel),
          $limit: 1,
        });

        if (response.data.length) {
          store.dispatch(
            showEntityView({ entityId: response.data[0].id, entityType: EntityType.COMPANY })
          );
          return { action: "navigate", stepId: "introB" };
        } else {
          store.dispatch(showGlobalSearch());
          return { action: "navigate", stepId: "introA" };
        }
      },
    },
    {
      id: "introA",
      description: messages.step01aDescription,
      key: "intro",
      title: messages.step01a,
      waitForEvent: {
        eventType: "internal",
        selector: "Clicked: Universal Search -> Clicked row",
      },
    },
    {
      id: "introB",
      description: messages.step01bDescription,
      key: "intro",
      title: messages.step01b,
    },
    {
      id: "menu",
      description: messages.step02Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "left",
      selector: "[class*=RecordActionsBar_container] [data-item-id=ellipsis]",
      title: messages.step02,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=RecordActionsBar_container] [data-item-id=ellipsis]",
      },
    },
    {
      id: "email-button-1",
      description: messages.step03Description,
      onBefore: async () => {
        // just letting the dropdown animation to complete
        await new Promise((resolve) => setTimeout(resolve, 100));

        const result = await checkEmailMenuItem();
        if (result) {
          // close drawer
          document
            .querySelector<HTMLElement>("[class*=RecordPane_container] .ant-drawer-close")
            ?.click();

          return result;
        }
      },
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "left",
      selector: ".ant-dropdown-menu-submenu[data-item-id=email]",
      title: messages.step03,
      waitForEvent: {
        eventType: "click",
        selector: ".ant-dropdown-menu-submenu[data-item-id=email]",
      },
    },
    {
      id: "email-button-2",
      description: messages.step04Description,
      onBefore: async () => {
        // just letting the dropdown animation to complete
        return new Promise((resolve) => setTimeout(resolve, 100));
      },
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "left",
      selector: ".ant-dropdown-menu-item[data-menu-id$=sendViaMMCEmail]",
      title: messages.step04,
      waitForEvent: {
        eventType: "click",
        selector: ".ant-dropdown-menu-item[data-menu-id$=sendViaMMCEmail]",
      },
    },
    {
      id: "email-button-3",
      onBefore: async () => {
        await new Promise<void>((resolve) =>
          waitForElement("[class*=mmc-js-modal-email]", resolve, {
            failureCallback: resolve,
            timeout: 1000,
          })
        );

        if (document.querySelector("[class*=mmc-js-modal-email-emails-missing]")) {
          return { action: "navigate", stepId: "email-modal-emails-missing" };
        }

        if (document.querySelector("[class*=mmc-js-modal-email-deals-selected]")) {
          return { action: "navigate", stepId: "email-modal-deals-selected" };
        }

        if (document.querySelector("[class*=mmc-js-modal-email-multiple-entities]")) {
          return { action: "navigate", stepId: "email-modal-multiple-entities" };
        }

        return { action: "navigate", stepId: "email-compose" };
      },
    },
    {
      id: "email-modal-emails-missing",
      description: messages.step06Description,
      onAfter: async () => {
        return Promise.resolve({ action: "navigate", stepId: "email-compose" });
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[class*=mmc-js-modal-email-emails-missing] .ant-btn-primary",
      title: messages.step06,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=mmc-js-modal-email-emails-missing] .ant-btn-primary",
      },
    },
    {
      id: "email-modal-deals-selected",
      description: messages.step09Description,
      onAfter: async () => {
        return Promise.resolve({ action: "navigate", stepId: "email-compose" });
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[class*=mmc-js-modal-email-deals-selected] .ant-btn-primary",
      title: messages.step09,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=mmc-js-modal-email-deals-selected] .ant-btn-primary",
      },
    },
    {
      id: "email-modal-multiple-entities",
      description: messages.step07Description,
      onAfter: async () => {
        return Promise.resolve({ action: "navigate", stepId: "email-compose" });
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[class*=mmc-js-modal-email-multiple-entities] .ant-btn-primary",
      title: messages.step07,
      waitForEvent: {
        eventType: "click",
        selector: "[class*=mmc-js-modal-email-multiple-entities] .ant-btn-primary",
      },
    },
    {
      id: "email-compose",
      description: messages.step08Description,
      title: messages.step08,
    },
    {
      id: "email-send",
      description: messages.step09Description,
      options: {
        showBackground: false,
      },
      selector: "[class*=EmailComposeModal_actionContainer] .ant-btn-primary",
      title: messages.step09,
    },
    {
      id: "completed",
      description: messages.step10Description,
      onAfter: closeComposeModal,
    },
  ],
};
